import { useState } from 'react'

import AutowidthInput from 'react-autowidth-input'
import { Link as RouterLink } from 'react-router-dom'
import { CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react'

import { useIsAdmin, useTeam } from '@/react-query/team'
import {
  useUpdateUserSettings,
  useUserSettings,
} from '@/react-query/user-settings'
import { PencilSvg } from '@/views/assets/PencilSvg'
import Card from '@/views/components/Card'

export const DeleteOldInterviews = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { data: userSettings, isLoading } = useUserSettings()

  const team = useTeam()
  const isTeamAdmin = useIsAdmin()
  const updateUserSettings = useUpdateUserSettings()
  const [oldInterviewsDays, setOldInterviewsDays] = useState(
    userSettings?.oldInterviewsDays || 30,
  )
  const [isEditingOldInterviewsDays, setIsEditingOldInterviewsDays] =
    useState(false)

  return (
    <Card w={'100%'} p={6} px={10}>
      <Heading size={'md'}>Privacy</Heading>
      {team.data ? (
        <HStack mt={6} w={'100%'} wrap={'wrap'}>
          <Text>Managed by your Team</Text>
          {isTeamAdmin && (
            <>
              &mdash;
              <Button
                as={RouterLink}
                variant={'brand-solid-white'}
                to={'/team'}
              >
                Go to Team Settings
              </Button>
            </>
          )}
        </HStack>
      ) : (
        <FormControl mt={6} display={'flex'} alignItems={'center'}>
          <Skeleton
            rounded={'1.25rem'}
            isLoaded={!isLoading}
            startColor="#f5f5f5"
            endColor="#d7d7d7"
          >
            <Switch
              id="enable-delete-old-interviews"
              onChange={(event) => {
                event.preventDefault()
                const value = event.target.checked
                if (value) {
                  onOpen()
                } else {
                  updateUserSettings.mutate({
                    enableDeleteOldInterviews: value,
                  })
                }
              }}
              isChecked={userSettings?.enableDeleteOldInterviews || false}
              size={'lg'}
              variant={'brand-black'}
            />
          </Skeleton>
          <Box mb="0" ml={'4'} w={'100%'} fontWeight={500}>
            <Text display={'inline'}>
              Automatically delete interviews after&nbsp;&nbsp;&nbsp;
            </Text>
            <Flex alignItems="center" display={'inline-flex'} fontWeight={600}>
              {isEditingOldInterviewsDays ? (
                <Input
                  w="auto"
                  mr={2}
                  flexGrow={0}
                  display={'inline'}
                  as={AutowidthInput}
                  value={oldInterviewsDays}
                  onChange={(e) => {
                    e.preventDefault()
                    const value = e.target.value?.replace(/[^0-9]/g, '')
                    if (value === '') {
                      setOldInterviewsDays(0)
                    } else {
                      const parsedValue = parseInt(value)
                      if (parsedValue > 99999) {
                        setOldInterviewsDays(99999)
                      } else {
                        setOldInterviewsDays(parsedValue)
                      }
                    }
                  }}
                  variant={'brand-solid-white'}
                />
              ) : (
                <Skeleton
                  rounded={'1.25rem'}
                  isLoaded={!isLoading}
                  startColor="#f5f5f5"
                  endColor="#d7d7d7"
                >
                  <Text>{oldInterviewsDays}&nbsp;&nbsp;</Text>
                </Skeleton>
              )}
              <IconButton
                onClick={async (e) => {
                  e.preventDefault()
                  if (isEditingOldInterviewsDays) {
                    updateUserSettings.mutate({
                      oldInterviewsDays,
                    })
                  }
                  setIsEditingOldInterviewsDays((isEditing) => !isEditing)
                }}
                ml={isEditingOldInterviewsDays ? 0 : '1.5px'}
                variant="brand-solid-white"
                icon={
                  isEditingOldInterviewsDays ? <CheckIcon /> : <PencilSvg />
                }
                aria-label="Edit old interviews days"
              />
              &nbsp;&nbsp;&nbsp;&nbsp;
            </Flex>
            <Text display={'inline'}>days</Text>
          </Box>
        </FormControl>
      )}
      {isOpen && (
        <ConfirmEnableDeleteOldInterviewsModal
          onClose={onClose}
          oldInterviewsDays={oldInterviewsDays}
        />
      )}
    </Card>
  )
}

const ConfirmEnableDeleteOldInterviewsModal = ({
  onClose,
  oldInterviewsDays,
}: {
  onClose: () => void
  oldInterviewsDays: number
}) => {
  const updateUserSettings = useUpdateUserSettings()

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Confirm</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            Are you sure you want to enable the deletion of old interviews?
            <br />
            <br />
            This will automatically delete all interviews that are older than{' '}
            {oldInterviewsDays} days, including existing ones.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={() => {
              updateUserSettings.mutate({
                oldInterviewsDays,
                enableDeleteOldInterviews: true,
              })
              onClose()
            }}
            variant="brand-solid"
          >
            Enable
          </Button>
          <Button
            variant="brand-solid-white"
            onClick={() => {
              onClose()
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
